import { orderBy } from "lodash";
import React from "react";
import { StyleProp, TextStyle, ViewStyle } from "react-native";

export type tColumnId = string;

type tRenderType = "Cell";

export type tColumn<T> = {
  id: tColumnId;
  header: string | ((options: { style?: StyleProp<TextStyle> }) => React.ReactNode);
  Cell: (
    options: Pick<tCellInstance<T>, "value"> & { row: T; depth: number; style?: StyleProp<TextStyle> },
  ) => React.ReactNode;
  accessor: (originalRow: T) => T[keyof T];
  containerStyle?: StyleProp<ViewStyle>;
  style?: StyleProp<ViewStyle>;
  isSortable?: boolean;
  alwaysVisible?: boolean;
  isHideable?: boolean;
};

export type tRow<T> = T & { subRows?: tRow<T>[] };

export type tHeaderInstance<T> = tColumn<T> & {
  isSorted: boolean;
  sortDirection?: tTableSortDirection;
  sort: () => void;
  containerStyle?: StyleProp<ViewStyle>;
  style: StyleProp<ViewStyle>;
};

export type tCellInstance<T> = {
  uniqueId: string;
  value: T[keyof T];
  render: (renderType: tRenderType) => React.ReactNode | undefined;
  style: StyleProp<TextStyle>;
};

export type tRowInstance<T> = T & {
  cells: tCellInstance<T>[];
  children?: tRowInstance<T>[];
};

export type tTableSortDirection = "asc" | "desc";

type tUseTableOptions<T> = {
  columns: tColumn<T>[];
  data: tRow<T>[];
  initialState?: { hiddenColumns?: tColumnId[]; sortedBy?: tColumnId; sortDirection?: tTableSortDirection };
};

type tUserTableInstance<T> = {
  headers: tHeaderInstance<T>[];
  rows: tRowInstance<T>[];
  state: { hiddenColumns: tColumnId[]; sortedBy: tColumnId; sortDirection: tTableSortDirection };
  setHiddenColumns: (hiddenColumns: tColumnId[]) => void;
  sort: (columnId: tColumnId, sortDirection?: tTableSortDirection) => void;
};

const prepareHeaders = <T>(
  columns: tColumn<T>[],
  hiddenColumns: tColumnId[],
  sortedBy: tColumnId,
  sortDirection: tTableSortDirection,
  sort: (columnId: tColumnId) => void,
): tHeaderInstance<T>[] => {
  return columns
    .filter((column) => !hiddenColumns.includes(column.id))
    .map((column) => ({
      ...column,
      isSorted: column.id === sortedBy,
      sortDirection,
      sort: () => sort(column.id),
      containerStyle: column.containerStyle,
      style: column.style,
    }));
};

const prepareRows = <T>(
  data: tRow<T>[],
  sortedBy: tColumnId,
  sortDirection: tTableSortDirection,
  headers: tHeaderInstance<T>[],
): tRowInstance<T>[] => {
  return orderBy(data, sortedBy, sortDirection).map((row) => ({
    ...row,
    children: row.subRows?.map((subRow) => {
      return {
        ...subRow,
        cells: headers.map((header) => {
          const value = header.accessor(subRow);
          return {
            uniqueId: `${header.id}-${1}`,
            value,
            style: header.style,
            render: (renderType: tRenderType) => {
              if (renderType === "Cell") return header?.Cell({ value, row: subRow, depth: 1, style: header.style });
              return undefined;
            },
          };
        }),
        subRows: [],
      };
    }),
    cells: headers.map((header) => {
      const value = header.accessor(row);
      return {
        uniqueId: `${header.id}-${0}`,
        value,
        style: header.style,
        render: (renderType: tRenderType) => {
          if (renderType === "Cell") return header?.Cell({ value, row, depth: 0, style: header.style });
          return undefined;
        },
      };
    }),
  }));
};

export const useTable = <T extends unknown>(options: tUseTableOptions<T>): tUserTableInstance<T> => {
  const [state, setState] = React.useState({
    hiddenColumns: options.initialState?.hiddenColumns ?? [],
    sortedBy: options.initialState?.sortedBy || options.columns[0].id,
    sortDirection: options.initialState?.sortDirection || "asc",
  });

  const sort = React.useCallback((column: tColumnId, _sortDirection?: tTableSortDirection) => {
    setState((prev) => {
      let sortDirection: tTableSortDirection;
      if (_sortDirection) {
        sortDirection = _sortDirection;
      } else if (prev.sortedBy !== column) {
        sortDirection = "asc";
      } else if (prev.sortDirection === "asc") {
        sortDirection = "desc";
      } else {
        sortDirection = "asc";
      }
      return { ...prev, sortedBy: column, sortDirection };
    });
  }, []);

  const setHiddenColumns = React.useCallback((newHiddenColumns: tColumnId[]) => {
    setState((prev) => ({ ...prev, hiddenColumns: newHiddenColumns }));
  }, []);

  const headers = React.useMemo(
    () => prepareHeaders(options.columns, state.hiddenColumns, state.sortedBy, state.sortDirection, sort),
    [options.columns, sort, state.hiddenColumns, state.sortDirection, state.sortedBy],
  );

  const rows = React.useMemo(
    () => prepareRows(options.data, state.sortedBy, state.sortDirection, headers),
    [headers, options.data, state.sortDirection, state.sortedBy],
  );

  return React.useMemo(
    () => ({
      headers,
      rows,
      setHiddenColumns,
      sort,
      state,
    }),
    [headers, rows, setHiddenColumns, sort, state],
  );
};
